// ============================================
// components/CommentSection.tsx
//
// 项目详情页评论区. 接后端 (Week 2 已就绪):
//   community-list-comments    → 一级评论列表 (游标分页, created_at desc)
//   community-comment-project  → 发评论 / 回复
//   community-delete-comment   → 删自己的评论 (软删)
//   community-list-comment-replies → 展开某条评论的回复 (created_at asc)
//
// 设计要点 (官网设计系统):
//   - 顶部输入框 (.input-field) + 发送按钮 (.btn-primary)
//   - 评论列表: 头像占位 + 昵称(后端暂只返 user_id, 用短号尾段做友好占位) + 时间 + 正文
//   - 一级评论可"展开 N 条回复" + 行内回复输入
//   - 自己的评论显示"删除"
//   - 乐观更新: 发送后立即插到列表顶部; 失败回滚
//   - 未登录: 输入框聚焦时提示"请先回 Studio 登录"
//
// 已知契约现实:
//   list-comments 只返 { short_id, user_id, content, mentions, marked_useful,
//     reply_count, created_at }, 不含昵称/头像. 故昵称用 user_id 末段占位,
//     头像统一占位. 后端若后续给评论补作者档, 这里替换 displayName/avatar 即可.
// ============================================

import { useEffect, useState, useCallback } from 'react';
import { callApi } from '../services/api';
import { authedCallApi, getCachedUser, isLoggedIn } from '../services/auth';
import { requireLogin } from '../services/loginPrompt';
import LoadingSpinner from './LoadingSpinner';
import ReportModal from './ReportModal';

interface CommentItem {
  short_id: string;
  user_id: string;
  author_nickname?: string;    // 后端返回的真实昵称
  author_avatar_url?: string;  // 后端返回的真实头像
  content: string;
  mentions?: string[];
  marked_useful?: boolean;
  reply_count?: number;
  created_at: number;
  // 前端附加:本地乐观插入标记
  _pending?: boolean;
}

interface ReplyState {
  open: boolean;
  loading: boolean;
  list: CommentItem[];
  nextCursor: string | null;
  draft: string;
  sending: boolean;
}

interface Props {
  projectShortId: string;
  onPosted?: (counted: boolean) => void;
}

const COMMENT_MAX = 500;

export default function CommentSection({ projectShortId, onPosted }: Props) {
  const [list, setList] = useState<CommentItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  // 每条一级评论的回复展开态
  const [replies, setReplies] = useState<Record<string, ReplyState>>({});

  // 举报: 存被举报评论的 short_id
  const [reportTarget, setReportTarget] = useState<string | null>(null);

  const me = getCachedUser();
  const myId = me?.user_id || null;

  const fetchPage = useCallback(
    async (reset: boolean, cur: string | null) => {
      const body: Record<string, unknown> = { project_short_id: projectShortId, limit: 20 };
      if (!reset && cur) body.cursor = cur;
      const r = await callApi<{ list: CommentItem[]; next_cursor: string | null }>(
        'community-list-comments',
        body,
        { noAuth: true },
      );
      if (r.ok && Array.isArray(r.list)) {
        const incoming = r.list as CommentItem[];
        setList((prev) => (reset ? incoming : [...prev, ...incoming]));
        setCursor((r.next_cursor as string | null) ?? null);
        setHasMore(!!r.next_cursor);
      } else if (reset) {
        setList([]);
        setHasMore(false);
      }
    },
    [projectShortId],
  );

  useEffect(() => {
    setLoading(true);
    setCursor(null);
    fetchPage(true, null).finally(() => setLoading(false));
  }, [fetchPage]);

  async function onLoadMore() {
    setLoadingMore(true);
    await fetchPage(false, cursor);
    setLoadingMore(false);
  }

  async function onSend() {
    const content = draft.trim();
    if (!content || sending) return;
    if (!requireLogin()) return;
    if (content.length > COMMENT_MAX) {
      setHint(`评论不超过 ${COMMENT_MAX} 字`);
      return;
    }
    setSending(true);
    setHint(null);

    // 乐观插入
    const tempId = 'c_temp_' + Date.now();
    const optimistic: CommentItem = {
      short_id: tempId,
      user_id: myId || 'me',
      author_nickname: me?.nickname || '',
      author_avatar_url: me?.avatar_url || '',
      content,
      reply_count: 0,
      created_at: Date.now(),
      _pending: true,
    };
    setList((prev) => [optimistic, ...prev]);
    setDraft('');

    const r = await authedCallApi<{ short_id: string; status?: string; pending_review?: boolean }>('community-comment-project', {
      project_short_id: projectShortId,
      content,
    });
    setSending(false);

    if (!r.ok) {
      // 回滚
      setList((prev) => prev.filter((c) => c.short_id !== tempId));
      setDraft(content);
      setHint(r.error === 'NO_TOKEN' ? '请先登录后再评论' : (r.message || '发送失败'));
      return;
    }
    // reviewing(疑似待审)的评论不显示也不计数; active 才显示+计数
    const isReviewing = r.pending_review === true || r.status === 'reviewing';
    if (isReviewing) {
      // 待审: 移除乐观插入的临时项(它不该显示), 提示用户
      setList((prev) => prev.filter((c) => c.short_id !== tempId));
      setHint('评论已提交, 审核通过后将显示');
    } else {
      // 用真实 short_id 替换临时项
      setList((prev) =>
        prev.map((c) =>
          c.short_id === tempId ? { ...c, short_id: String(r.short_id), _pending: false } : c,
        ),
      );
    }
    onPosted?.(!isReviewing);   // 传 counted: 仅 active 评论才让外层计数 +1
  }

  async function onDelete(c: CommentItem) {
    if (!window.confirm('确定删除这条评论?')) return;
    // 乐观移除
    const snapshot = list;
    setList((prev) => prev.filter((x) => x.short_id !== c.short_id));
    const r = await authedCallApi<{ deleted: boolean }>('community-delete-comment', {
      comment_short_id: c.short_id,
    });
    if (!r.ok) {
      setList(snapshot); // 回滚
      setHint(r.message || '删除失败');
    }
  }

  // ── 回复展开/收起 ──
  async function toggleReplies(c: CommentItem) {
    const cur = replies[c.short_id];
    if (cur?.open) {
      setReplies((prev) => ({ ...prev, [c.short_id]: { ...cur, open: false } }));
      return;
    }
    // 打开 + 首次加载
    setReplies((prev) => ({
      ...prev,
      [c.short_id]: {
        open: true,
        loading: !cur,
        list: cur?.list || [],
        nextCursor: cur?.nextCursor ?? null,
        draft: cur?.draft || '',
        sending: false,
      },
    }));
    if (!cur) {
      const r = await callApi<{ list: CommentItem[]; next_cursor: string | null }>(
        'community-list-comment-replies',
        { comment_short_id: c.short_id, limit: 20 },
        { noAuth: true },
      );
      setReplies((prev) => ({
        ...prev,
        [c.short_id]: {
          open: true,
          loading: false,
          list: r.ok && Array.isArray(r.list) ? (r.list as CommentItem[]) : [],
          nextCursor: (r.next_cursor as string | null) ?? null,
          draft: '',
          sending: false,
        },
      }));
    }
  }

  async function onSendReply(parent: CommentItem) {
    const st = replies[parent.short_id];
    const content = (st?.draft || '').trim();
    if (!content || st?.sending) return;
    if (!requireLogin()) return;
    setReplies((prev) => ({ ...prev, [parent.short_id]: { ...st!, sending: true } }));

    const r = await authedCallApi<{ short_id: string; status?: string; pending_review?: boolean }>('community-comment-project', {
      project_short_id: projectShortId,
      content,
      parent_comment_id: parent.short_id,
    });

    if (!r.ok) {
      setReplies((prev) => ({ ...prev, [parent.short_id]: { ...st!, sending: false } }));
      setHint(r.error === 'NO_TOKEN' ? '请先登录后再回复' : (r.message || '回复失败'));
      return;
    }

    const isReviewing = r.pending_review === true || r.status === 'reviewing';
    if (isReviewing) {
      // 待审回复: 不插入显示, 不加计数, 提示用户
      setReplies((prev) => ({ ...prev, [parent.short_id]: { ...st!, sending: false, draft: '' } }));
      setHint('回复已提交, 审核通过后将显示');
      onPosted?.(false);
      return;
    }

    const newReply: CommentItem = {
      short_id: String(r.short_id),
      user_id: myId || 'me',
      author_nickname: me?.nickname || '',
      author_avatar_url: me?.avatar_url || '',
      content,
      created_at: Date.now(),
    };
    setReplies((prev) => ({
      ...prev,
      [parent.short_id]: {
        ...st!,
        sending: false,
        draft: '',
        list: [...st!.list, newReply],
      },
    }));
    // 父评论回复数 +1
    setList((prev) =>
      prev.map((x) =>
        x.short_id === parent.short_id ? { ...x, reply_count: (x.reply_count || 0) + 1 } : x,
      ),
    );
    onPosted?.(true);
  }

  return (
    <div>
      {/* 发评论 */}
      <div className="bg-white border border-ink-300/40 rounded-2xl p-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => { if (!isLoggedIn()) setHint('登录后即可发表评论'); }}
          placeholder="友善地聊聊这个作品…"
          rows={3}
          maxLength={COMMENT_MAX}
          className="input-field resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-ink-400 tabular-nums">
            {draft.length}/{COMMENT_MAX}
          </span>
          <button className="btn-primary !py-2 !px-5" disabled={sending || !draft.trim()} onClick={onSend}>
            {sending ? '发送中…' : '发表评论'}
          </button>
        </div>
        {hint && <p className="text-xs text-amber-600 mt-2">{hint}</p>}
      </div>

      {/* 列表 */}
      <div className="mt-5 space-y-4">
        {loading ? (
          <div className="py-8 flex justify-center"><LoadingSpinner small /></div>
        ) : list.length === 0 ? (
          <p className="text-center text-ink-400 text-sm py-8">还没有评论,来抢沙发吧~</p>
        ) : (
          list.map((c) => {
            const rs = replies[c.short_id];
            return (
              <div key={c.short_id} className={`flex gap-3 ${c._pending ? 'opacity-60' : ''}`}>
                <Avatar id={c.user_id} name={c.author_nickname} avatarUrl={c.author_avatar_url} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-ink-900">{displayName(c.user_id, c.author_nickname)}</span>
                    <span className="text-xs text-ink-400">{fmtTime(c.created_at)}</span>
                    {c.marked_useful && <span className="pill bg-gold-soft text-gold-700">作者觉得有用</span>}
                  </div>
                  <p className="text-sm text-ink-700 mt-1 whitespace-pre-wrap break-words">{c.content}</p>

                  <div className="flex items-center gap-4 mt-1.5 text-xs text-ink-400">
                    <button className="hover:text-gold-600 transition-colors" onClick={() => toggleReplies(c)}>
                      {rs?.open ? '收起' : (c.reply_count ? `${c.reply_count} 条回复` : '回复')}
                    </button>
                    {myId && c.user_id === myId && !c._pending && (
                      <button className="hover:text-red-500 transition-colors" onClick={() => onDelete(c)}>
                        删除
                      </button>
                    )}
                    {(!myId || c.user_id !== myId) && !c._pending && (
                      <button className="hover:text-red-500 transition-colors" onClick={() => setReportTarget(c.short_id)}>
                        举报
                      </button>
                    )}
                  </div>

                  {/* 回复区 */}
                  {rs?.open && (
                    <div className="mt-3 pl-3 border-l-2 border-ink-300/40 space-y-3">
                      {rs.loading ? (
                        <div className="py-2"><LoadingSpinner small /></div>
                      ) : (
                        rs.list.map((rep) => (
                          <div key={rep.short_id} className="flex gap-2.5">
                            <Avatar id={rep.user_id} name={rep.author_nickname} avatarUrl={rep.author_avatar_url} small />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-ink-900">{displayName(rep.user_id, rep.author_nickname)}</span>
                                <span className="text-[11px] text-ink-400">{fmtTime(rep.created_at)}</span>
                              </div>
                              <p className="text-sm text-ink-700 mt-0.5 whitespace-pre-wrap break-words">{rep.content}</p>
                            </div>
                          </div>
                        ))
                      )}

                      {/* 行内回复输入 */}
                      <div className="flex items-center gap-2">
                        <input
                          value={rs.draft}
                          onChange={(e) =>
                            setReplies((prev) => ({ ...prev, [c.short_id]: { ...rs, draft: e.target.value } }))
                          }
                          placeholder="回复…"
                          maxLength={COMMENT_MAX}
                          className="input-field !py-2 text-sm"
                        />
                        <button
                          className="btn-secondary !py-2 !px-4 shrink-0"
                          disabled={rs.sending || !rs.draft.trim()}
                          onClick={() => onSendReply(c)}
                        >
                          {rs.sending ? '…' : '回复'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {hasMore && (
          <div className="flex justify-center pt-2">
            <button className="btn-secondary !py-2" onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore ? '加载中…' : '查看更多评论'}
            </button>
          </div>
        )}
      </div>

      {/* 举报弹窗 */}
      {reportTarget && (
        <ReportModal
          targetType="comment"
          targetShortId={reportTarget}
          onClose={() => setReportTarget(null)}
          onDone={(msg) => setHint(msg)}
        />
      )}
    </div>
  );
}

function Avatar({ id, name, avatarUrl, small }: { id: string; name?: string; avatarUrl?: string; small?: boolean }) {
  const sz = small ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  // 真实头像: 仅用 http(s) 图片; 旧的 dicebear: 等约定值忽略, 走字母头像
  const isImg = !!avatarUrl && /^https?:\/\//.test(avatarUrl);
  if (isImg) {
    return <img src={avatarUrl} alt="" className={`${sz} rounded-full object-cover shrink-0`} />;
  }
  const letter = (name || id || '?').replace(/^u(ser)?_/, '').slice(0, 1).toUpperCase();
  return (
    <span
      className={`grid place-items-center ${sz} rounded-full text-white font-bold shrink-0`}
      style={{ background: 'linear-gradient(135deg,#f0c14b,#e0a020)' }}
    >
      {letter}
    </span>
  );
}

// 优先用后端返回的真实昵称; 无则用 user_id 末段占位
function displayName(userId: string, nickname?: string): string {
  if (nickname && nickname.trim()) return nickname.trim();
  if (!userId) return '匿名用户';
  const tail = userId.replace(/^u(ser)?_/, '').slice(-4);
  return `创客${tail}`;
}

function fmtTime(ts: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return '刚刚';
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} 天前`;
  const date = new Date(ts);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
